// /**
//  * Ordered List
//  */

// React
import React, { useEffect } from 'react';

// React DND
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  ResponderProvided,
  DraggableProvided,
  DroppableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';

// UI
import theme from '../../theme';
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Grid,
  Button,
  Box,
  Tooltip
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiFileDocumentOutline,
  mdiHistory,
  mdiDragHorizontalVariant,
  mdiPlusBoxMultipleOutline,
  mdiPencil,
  mdiClose,
} from '@mdi/js';

// Helper
import { checkMatchMedia, translate } from '../../utils/helpers';
import jMoment from 'moment-jalaali';

// Type
import { Order, NonPostOrder } from '../orders/interfaces';

type Data = NonPostOrder;
type Props = {
  data: any;
  loading: boolean;
  handleOpenSelectDriver: (data: Order[]) => void;
  editMode: boolean;
};
type Ord = 'asc' | 'desc';

interface HeadCell {
  id: keyof Data;
  label: string;
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '@media (max-width: 768px)': {
        fontSize: 10,
      },
    },
    body: {
      fontSize: 14,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  })
)(TableCell);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    icon: {
      marginRight: theme.spacing(1),
    },
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    paper: {
      width: '100%',
    },
    table: {
      // minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    columnText: {
      padding: '0 10px',
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    container: {
      maxHeight: 650,
      borderRadius: '4px',
    },
    // Fully visible for active icons
    activeSortIcon: {
      opacity: 1,
      color: `${theme.palette.secondary.main}!important`,
    },
    // Half visible for inactive icons
    inactiveSortIcon: {
      opacity: 0.4,
    },
    iconButton: {
      padding: 10,
      position: 'relative',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    changStatusIcon: {
      marginRight: '10px',
      position: 'absolute',
    },
    changStatusBox: {
      display: 'flex',
      textAlign: 'left',

      fontWeight: 400,
      fontSize: '14px',
    },
    renderValue: {
      paddingLeft: '32px',
    },
    radioGroup: {
      margin: '-8px 0',
    },
    radioGroupItem: {
      borderBottom: '1px solid #D8D8D8',
      marginRight: '-8px',
      padding: '8px 16px',
      '&:last-child': {
        borderBottom: '0px',
      },
    },
  })
);

interface EnhancedTableToolbarProps {
  numSelected: number;
  selected: any;
  data: any;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const classes = useStyles();
  let headCells: HeadCell[] = [
    { id: 'customerCity', label: 'شهر' },
    { id: 'id', label: 'شناسه سفارش' },
    { id: 'sourceName', label: 'مرکز/انبار' },
    {
      id: 'deliveryDate',
      label: 'تاریخ تحویل',
    },
    {
      id: 'deliveryStartTime',
      label: 'ساعت تحویل',
    },
    {
      id: 'weight',
      label: 'وزن',
    },
    {
      id: 'length',
      label: 'ابعاد',
    },
    { id: 'status', label: 'وضعیت' },
  ];

  return (
    <TableHead>
      <TableRow>
        <StyledTableCell />
        {headCells.map(headCell => (
          <StyledTableCell
            key={headCell.id}
            align="center"
            data-cy={headCell.id}
          >
            <span className={classes.columnText}>{headCell.label}</span>
          </StyledTableCell>
        ))}
        <StyledTableCell />
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const classes = useStyles();
  const [localItems, setLocalItems] = React.useState<Array<Order>>([]);

  useEffect(() => {
    setLocalItems(props.data);
  }, [props.data]);

  const handleDragEnd = (result: DropResult, provided?: ResponderProvided) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    setLocalItems((prev: any) => {
      const temp = [...prev];
      const d = temp[result.destination!.index];
      temp[result.destination!.index] = temp[result.source.index];
      temp[result.source.index] = d;

      return temp;
    });
  };

  const HtmlTooltip = withStyles((theme: Theme) => ({
    tooltip: {
      backgroundColor: '#f5f5f9',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      border: '1px solid #dadde9',
    },
  }))(Tooltip);

  const handleRemoveItem = (id: number) => {

    const selectedIndex = localItems.findIndex(el => el.id === id);
    const prevState = [...localItems];
    if (selectedIndex > -1) {
      prevState.splice(selectedIndex, 1);
      setLocalItems(prevState);
    }
    if (props?.editMode) {
      props.changeStatus(id, prevState)
    }
  };
  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TableContainer className={classes.container}>
          <Table
            stickyHeader
            className={classes.table}
            aria-labelledby="tableTitle"
            size={checkMatchMedia('(max-width: 1024px)') ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead classes={classes} rowCount={localItems.length} />
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="droppable" direction="vertical">
                {(droppableProvided: DroppableProvided) => (
                  <TableBody
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                  >
                    {localItems.map((row: Order, index: number) => (
                      <Draggable
                        key={row.id}
                        draggableId={row.id + ''}
                        index={index}
                      >
                        {(
                          draggableProvided: DraggableProvided,
                          snapshot: DraggableStateSnapshot
                        ) => {
                          return (
                            <TableRow
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                              style={{
                                ...draggableProvided.draggableProps.style,
                                background: snapshot.isDragging
                                  ? 'rgba(245,245,245, 0.75)'
                                  : 'none',
                              }}
                            >
                              {/* note: `snapshot.isDragging` is useful to style or modify behaviour of dragged cells */}
                              <StyledTableCell align="left">
                                <div {...draggableProvided.dragHandleProps}>
                                  <Icon
                                    path={mdiDragHorizontalVariant}
                                    size={1}
                                    color={theme.palette.green.main}
                                  />
                                </div>
                              </StyledTableCell>
                              <StyledTableCell
                                align="center"
                                style={{
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                                data-cy="row-id"
                              >
                                <HtmlTooltip
                                  title={
                                    row?.customerAddress + ' __ ' + row?.customerName
                                  }
                                >
                                  <span>
                                    {`${row.customerCity
                                      }-${row?.customerAddress?.substring(0, 20)}`}
                                  </span>
                                </HtmlTooltip>
                              </StyledTableCell>
                              <StyledTableCell scope="row" align="center">
                                {row.id}
                              </StyledTableCell>
                              <StyledTableCell scope="row" align="center">
                                {row?.fulfillmentWarehouseName
                                  ? row?.fulfillmentWarehouseName
                                  : row?.sourceName}
                              </StyledTableCell>
                              <StyledTableCell align="center">
                                <span>
                                  {jMoment(row.deliveryDate, 'YYYY/M/D')
                                    .locale('fa')
                                    .format('jYY/jMM/jDD')}
                                </span>
                              </StyledTableCell>
                              <StyledTableCell align="center">
                                <span>
                                  {`${jMoment(row.deliveryStartTime, 'HH:mm:ss')
                                    .locale('fa')
                                    .format('HH:mm')}`}
                                  {`- ${jMoment(row.deliveryEndTime, 'HH:mm:ss')
                                    .locale('fa')
                                    .format('HH:mm')}`}
                                </span>
                              </StyledTableCell>
                              <StyledTableCell scope="row" align="center">
                                {row.weight}
                              </StyledTableCell>
                              <StyledTableCell scope="row" align="center">
                                {row.length ? `${row.length} x ${row.width} x ${row.height}` : '-'}
                              </StyledTableCell>
                              <StyledTableCell>
                                {translate(row.status, 'order')}
                              </StyledTableCell>
                              <StyledTableCell align="right">
                                <div>
                                  <IconButton
                                    aria-label="remove-item"
                                    size="small"
                                    title="حذف آیتم"
                                    data-cy="remove"
                                    onClick={() => handleRemoveItem(row.id)}
                                  >
                                    <Icon
                                      path={mdiClose}
                                      size={1}
                                      color={theme.palette.error.main}
                                    />
                                  </IconButton>
                                  <a
                                    target="_blank"
                                    href={`/nonPost-orders/${row.id}`}
                                  >
                                    <IconButton
                                      aria-label="details"
                                      size="small"
                                      title="جزئیات سفارش"
                                      data-cy="details"
                                    >
                                      <Icon
                                        path={mdiFileDocumentOutline}
                                        size={1}
                                        color={theme.palette.green.main}
                                      />
                                    </IconButton>
                                  </a>
                                  <a
                                    target="_blank"
                                    href={`/orders/${row.id}/allLogs`}
                                  >
                                    <IconButton
                                      size="small"
                                      title="تاریخچه سفارش"
                                    >
                                      <Icon path={mdiHistory} size={1} />
                                    </IconButton>
                                  </a>
                                </div>
                              </StyledTableCell>
                            </TableRow>
                          );
                        }}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </DragDropContext>
          </Table>
        </TableContainer>
      </Paper>
      <Box mb={4} />
      <Grid container direction="row" justify="flex-end" alignItems="center">
      <Button
          variant="contained"
          color="secondary"
          aria-label="assignment"
          size="large"
          style={{marginLeft: "8px"}}
          onClick={() => props.handleOpenSelectDriver(localItems,"name")}
        >
          <Icon
            path={props.editMode ? mdiPencil : mdiPlusBoxMultipleOutline}
            size={1}
            className={classes.icon}
          />
          ایجاد باندل بدون راننده
        </Button>
        <Button
          variant="contained"
          color="secondary"
          aria-label="assignment"
          size="large"
          onClick={() => props.handleOpenSelectDriver(localItems,"driver")}
        >
          <Icon
            path={props.editMode ? mdiPencil : mdiPlusBoxMultipleOutline}
            size={1}
            className={classes.icon}
          />
          {props.editMode ? 'ویرایش مرسوله' : 'تخصیص سفارش به راننده'}
        </Button>
      </Grid>
    </div>
  );
}
